<script>

	import { onMount, onDestroy, tick, createEventDispatcher } from 'svelte';
	import { crossfade, fade, fly } from 'svelte/transition';
	import { flip } from 'svelte/animate';
	import { quintOut } from 'svelte/easing';

	import moment from 'moment-timezone';

	import Avatar from "../../../ui/Avatar.svelte";
	// import Emoji from "./Chat/Emoji.svelte";
	// import Giphy from "./Chat/Giphy.svelte";
	import GifPreview from "./Chat/GifPreview.svelte";
	import Menu from "../../../ui/Menu.svelte";
	import Button from "../../../ui/Button.svelte";
	import Dialog from "../../../ui/Dialog.svelte";
	import Slate from "../../../ui/Slate.svelte";

	import { event, attendee, attendeeLikes, bigError, shadowbans, syncClient, session, spotlight, showSettings, settings } from '../../../lib/stores.js';
	import { text_to_html } from '../../../lib/html.js';
	import { showDate } from '../../../lib/dt.js';
	import { postServerData } from '../../../lib/prelude.js';
	import { autoblur } from '../../../lib/autoblur.js';
	import { randomIntFromInterval } from '../../../lib/utils.js';
	import { db } from "../../../lib/db.js";
	// import { logInteraction } from '../../../lib/usage.js';

	const dispatch = createEventDispatcher();

	const [send, receive] = crossfade({
		duration: 0,
		fallback(node, params) {
			return {
				duration: 300,
				easing: quintOut,
				css: t => `
					transform: scale(${1 - ((1 - t) / 32)});
					opacity: ${t}
				`
			};
		}
	});

	export let isPrivate = false;
	export let channelRef = isPrivate ? $session.privateChannel : $session.syncChannel;
	export let slateText = 'Say something when the session is live and it will appear here.';
	// export let emojis = true;

	export let fullscreenSend = false; // so we can bind

	let channel = undefined;
	let messages = [];

	let remaining = 200;
	let messageText = '';
	let gif = null;

	let mounted = false;
	let ready = false;

	// let emojiPanel = false;
	let giphyPanel = false;

	let msgs;
	let ta;

	let pinHeight = 0;
	// let selectedMsg = null;

	let avatarMenu = null;
	let avatars = {};

	let participants = {};

	// let mod = ($attendee.type == 'login') || ($backstage.includes($session.ref));
	// let mod = ($attendee.type == 'login');
	let mod = false;

	$: mod = ($settings && $settings.room && (($settings.mode == 'presenter') || ($settings.mode == 'av'))) ? false : true;

	let deleteMsgDialog = null;

	let mastheadHeight = 0;

	onMount(async () => {

		mastheadHeight = document.getElementById('masthead').offsetHeight;

		try {

			console.log("opening session channel (chat)", channelRef);
			channel = await $syncClient.list(channelRef);

			channel.on('itemAdded', function(i) {
				if (i.item.data.type == 'chat') {
					addMessage(i.item);
				}
			});

			channel.on('itemUpdated', function(i) {
				if (i.item.data.type == 'chat') {
					updateMessage(i.item);
				}
			});

			channel.on('itemRemoved', function(i) {
				if (i.previousItemData.type == 'chat') {
					deleteMessage(i.index);
				}
			});

			await getChatItems();
			
		} catch (e) {

			console.log("Sync error", e, channelRef);
			
		}

		mounted = true;

		setTimeout(() => {
			if (msgs) {
				setPinHeight();
			}
		}, 200);

		let delay = messages.length ? 500 : 0;

		setTimeout(() => {
			if (msgs) {
				msgs.parentNode.scrollBy(0, 1000000);
				ready = true;
			}
		}, delay);

		// console.log('chat mount', messages.length, delay);

	});

	onDestroy(() => {
		// console.log("closing session channel (chat)");
		if (channel) channel.close();
	});

	function chatPageHandler(paginator) {
		paginator.items.forEach(function(item) {
			console.log('paginator', {item});
			if (item.data.type == 'chat') {
				addMessage(item);
			}
		});
		return paginator.hasNextPage ? paginator.nextPage().then(chatPageHandler) : null;
	};

	async function getChatItems() {
		let paginator;
		try {
			paginator = await channel.getItems({
				from: 0,
				order: 'asc',
				pageSize: 100
			});
		} catch (error) {
			if (error.message.match('Maximum attempt')) {
				// back off and try again later...
				const smear = 1000 * randomIntFromInterval(5,10);
				setTimeout(async () => {
					await getChatItems();
				}, smear);
			} else {
				console.error('getChatItems failed', error);
			}
		}

		if (paginator) await chatPageHandler(paginator);

	}

	async function addMessage(item) {
		let msg = processMessage(item);
		if (msg.sender) {
			if (!participants[msg.sender]) participants[msg.sender] = await db.attendees.get(msg.sender);
		}
		messages.push(msg);
		messages = messages;
		console.log('addMessage', participants[msg.sender]);
		scrollToEnd();
	}

	async function updateMessage(item) {
		// console.log('updateMessage', item.data);
		const idx = item.index;
		let msg = processMessage(item);
		for (let [i,r] of messages.entries()) {
			if (r.index == idx) {
				// console.log('replacing', idx, reply);
				messages[i] = msg;
				break;
			}
		}
		messages = messages;
		// if (msg.pinned) {
		// 	setPinHeight(msg.index);
		// }
		setPinHeight();
	}

	function deleteMessage(idx) {
		messages = messages.filter(m => {
			if (m.index != idx) return m;
		});
	}

	function processMessage(item) {
		let gif = (item.data.message.match(/^https\:\/\/media\d+\.giphy\.com/)) ? item.data.message : null;
		let html = gif ? null : text_to_html(item.data.message);
		let sent = item.data.dt ? moment.unix(item.data.dt) : moment(item.dateUpdated);
		let msg = {
			revision: item.index + '/' + item.revision,
			index: item.index,
			gif: gif,
			message: html,
			sent: sent,
			sender: item.data.sender,
			likes: item.data.likes ? item.data.likes : 0,
			liked: $attendeeLikes.includes(channelRef + '/' + item.index) ? true : false,
			pinned: item.data.pinned ? true : false,
			urgent: item.data.urgent ? true : false
		};
		return msg;
	}

	async function sendMessage(e) {

		fullscreenSend = false;

		if (gif || ((messageText != '') && (remaining >= 0))) {
			// logInteraction('now/chat');
			await postServerData('onstage/chat', {
				channelRef: channelRef,
				message: gif ? gif : messageText
			});
			if (!$bigError) {
				messageText = '';
				gif = null;
				await tick();
				resize({ target: ta });
				// ta.focus();
				ta.blur();
			}
		}

	}

	function setGif(e) {
		gif = e.detail.gif;
	}

	function unsetGif() {
		gif = null;
	}

	function checkMessage(e) {
		if (e.code == 'Enter') {
			e.target.form.dispatchEvent(new Event("submit", {cancelable: true}));
			e.preventDefault();
		}
	}

	function scrollToEnd() {
		if (mounted) {
			// check that we haven't scrolled up by more than a message or so...
			let scrolledBack = false;
			if (msgs && msgs.lastElementChild) {
				let scrollDiff = msgs.scrollHeight - (msgs.parentNode.scrollHeight + msgs.scrollTop);
				// console.log(scrollDiff, msgs.lastElementChild.scrollHeight);
				if (scrollDiff > (msgs.lastElementChild.scrollHeight > 200 ? msgs.lastElementChild.scrollHeight : 200)) {
					scrolledBack = true;
				}
				if (!scrolledBack) {
					tick().then(() => {
						msgs.lastElementChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
						// console.log(msgs.parentNode.scrollHeight, msgs.scrollHeight, msgs.scrollTop)
					});
				}
			}
		}
	}

	async function toggleLike(msg) {
		// Update immediately; Sync revision will update again...
		if (msg.liked) {
			msg.likes--;
			msg.liked = false;
		} else {
			msg.likes++;
			msg.liked = true;
			// logInteraction('now/chat/likes');
		}
		// selectedMsg = null;
		const d = new Date;
		msg.revision = msg.index + '/' + d.getTime();
		messages = messages;
		// console.log(reply);
		await postServerData('onstage/likes', {
			channelRef: channelRef,
			messageIndex: msg.index
		});
	}

	async function togglePin(msg) {

		let pin;
		let unpin;

		if (msg.pinned) {
			unpin = msg.index;
			msg.pinned = false;
			pinHeight = 0;
		} else {
			for (let m of messages) {
				if (m.pinned) {
					unpin = m.index;
					m.pinned = false;
				}
			}
			pin = msg.index;
			msg.pinned = true;
		}

		postServerData('onstage/chat/pin', {
			channelRef: channelRef,
			pin: pin,
			unpin: unpin
		});

		// selectedMsg = null;
		messages = messages;

		if (msg) {
			setPinHeight(msg.index);
		}

	}

	function setPinHeight(index) {
		// console.log('setPinHeight', index);
		if (!mounted) return;
		if (index === undefined) {
			for (const m of messages) {
				if (m.pinned) {
					index = m.index;
					// console.log({index});
					break;
				}
			}
		}
		tick().then(() => {
			pinHeight = 0;
			if (index != undefined) {
				const p = document.getElementById('msg-' + index);
				if (p && p.classList.contains('pinned')) {
					pinHeight = p.offsetHeight;
				}
			}
			// console.log({pinHeight});
		});
	}

	function actionDelete(msg) {
		// $modal = {
		// 	message: 'Please confirm you wish to delete this message.',
		// 	details: [],
		// 	opts: [
		// 		{
		// 			name: "Delete",
		// 			display: {
		// 				mini: true,
		// 				red: true
		// 			},
		// 			f: () => {
		// 				doDelete(msg);
		// 			}
		// 		},
		// 		{
		// 			name: "Cancel",
		// 			display: {
		// 				mini: true,
		// 				ghost: true
		// 			},
		// 			f: () => {}
		// 		},
		// 	]
		// };
		deleteMsgDialog = {
			text: "Are you sure you want to delete this message?",
			actions: [
				{
					label: "Confirm",
					dispatch: "confirm"
				},
				{
					label: "Cancel",
					ghost: true,
					dispatch: "escape"
				}
			],
			msg: msg
		};
	}

	async function doDelete(msg) {
		// selectedMsg = null;
		deleteMsgDialog = null;
		await postServerData('onstage/chat/delete', {
			channelRef: channelRef,
			index: msg.index
		});
	}

	function resize({ target }) {
		target.style.height = "1px";
		target.style.height = (+target.scrollHeight)+"px";
		scrollToEnd();
	}

	function autoresize(el) {
		resize({ target: el });
		el.style.overflow = 'hidden';
		el.addEventListener('input', resize);

		return {
			destroy: () => el.removeEventListener('input', resize)
		}
	}

	function addEmoji(e) {
		messageText += e.detail;
	}

	$: remaining = 200 - messageText.length;

	// $: if (emojiPanel || messageText) {
	// 	closeGiphy();
	// }
	$: if (fullscreenSend || messageText) {
		closeGiphy();
	}

	function closeGiphy() {
		giphyPanel = false;
	}

	// $: if (giphyPanel) {
	// 	closeEmojis();
	// }

	// function closeEmojis() {
	// 	emojiPanel = false;
	// }

	function handleWindowClick() {
		// selectedMsg = null;
		// closeEmojis();
		closeGiphy();
	}

	// function handleKeydown(event){
	// 	if (event.key === 'Escape') {
	// 		selectedMsg = null;
	// 	}
	// }

	function closeAvatarMenus() {
		for (const [k,v] of Object.entries(avatars)) {
			if (k != avatarMenu) {
				if (v) v.closeMenu();
			}
		}
	}

	function toggleHighlight(r) {
		if (studioHighlight && (studioHighlight.t == 'chat') && (studioHighlight.r == r)) {
			dispatch('highlight', null);
		} else {
			dispatch('highlight', { t: 'chat', r: r });
		}
	}

	async function makeFullscreen(e) {
		if (!fullscreenSend) {
			e.preventDefault();
			fullscreenSend = true;
			await tick();
			ta.focus();
		}
	}

	function toggleSpotlight(m) {
		setTimeout(() => {
			if ($spotlight && ($spotlight.type == 'chat') && ($spotlight.index == m.index)) {
				$spotlight = null;
			} else {
				$spotlight = {
					type: 'chat',
					message: m.message,
					sender: m.sender,
					index: m.index,
					session: $session.ref
				};
			}
		}, 100);
	}

	$: if ($shadowbans) {
		setPinHeight();
		scrollToEnd();
	}

</script>

<style>
	.wrap {
		position: absolute;
		inset:  0;
		display: flex;
		flex-direction: column;
	}
	.chat {
		flex: 1 1 auto;
		overflow: auto;
		padding: 0 0 0.5rem 0;
		transition: padding-top 0.3s ease-out;
		/*scrollbar-width: thin;*/
		-ms-overflow-style: none;
		scrollbar-width: none;
		position: relative;
	}
	.chat::-webkit-scrollbar {
		display: none;
	}

	@media (max-width: 955px) {
		.chat {
			padding: 0 0 7rem 0;
		}
	}

	:global(.report) .chat {
		margin: 0 -1rem;
	}

	.chat > div {
		display: flex;
		flex-direction: column;
		opacity: 0;
		transition: opacity 0.2s ease-in;
	}
	.chat.ready > div {
		opacity: 1;
	}
	.msg {
		position: relative;
		/*background: var(--blend-10);*/
		padding: 0.3rem 0.5rem 0.1rem 0.5rem;
		/*background: var(--bgColor, #fff);*/
		box-sizing: border-box;
	}
	.msg:before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 0.6rem;
		background: var(--bgColor, #fff);
	}

	.private .msg:before {
		background: var(--blend-10);
	}

	/*@media (max-width: 700px) {
		.private .msg:before {
			background: var(--blend-20);
		}
	}*/

	.msg > div {
		position: relative;
/*		background: var(--bgColor, #fff);*/
		color: var(--textColor);
		border-radius: 4px;
		padding: 0.5rem 0.5rem 0.5rem 3.4rem;
		word-wrap: break-word;
		margin: 0 0.2rem;
		background: var(--blend-05);
	}
	/*.msg + .msg {
		margin-top: 0.5rem;
	}*/

	/*.msg > div:hover {
		background: var(--blend-05);
	}*/

	.msg.organiser > div {
		background: var(--accentBlend-10);
	}
	/*.msg.organiser > div:hover {
		background: var(--accentBlend-20);
	}*/

	.private .msg > div,
	.private .msg.organiser > div {
		background: var(--blend-10);
	}

	.chat :global(.avatar) {
		position: absolute;
		top: 0.6rem;
		left: 0.7rem;
		width: 2rem;
		height: 2rem;
	}
	.chat :global(p) {
		font-size: 0.9rem;
		line-height: 1.4;
	}
	.person {
		margin-bottom: 0.2rem;
		font-size: 0.9rem;
	}
	.person strong {
		font-weight: 700;
	}
	.person span {
		display: inline-block;
		margin-left: 0.3rem;
		opacity: 0.6;
	}
	.person em {
		display: inline-block;
		margin-left: 0.1rem;
		color: var(--accentColor);
		font-style: normal;
		font-weight: 600;
	}
	.person svg {
		display: inline-block;
		margin-left: 0.2rem;
		width: 0.8rem;
	}
	.person svg path {
		fill: var(--accentColor);
	}

	/*.msg .actions {
		display: none;
		position: absolute;
		top: -0.3rem;
		right: -0.2rem;
		z-index: 100;
	}

	.msg:hover .actions,
	.msg.selected .actions {
		display: flex;
		gap: 0.5rem;
		align-items: center;
		justify-content: center;
	}

	.msg .actions > div {
		background: var(--bgColor);
		border-radius: 3px;
		height: 1.6rem;
		padding: 0.1rem 0.5rem;
		box-shadow: 0 1px 4px var(--darkShadow), 0 0 0 1px var(--blend-05);
		display: flex;
		gap: 0.8rem;
	}

	.msg .actions button {
		position: relative;
		box-sizing: border-box;
		border: none;
		background: transparent;
		color: var(--textColor);
		width: 1.6rem;
		height: 1.6rem;
	}

	.msg .actions button:hover {
		color: var(--accentColor);
		cursor: pointer;
	}

	.msg .actions button svg {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	.msg.openMenu .actions {
		display: none !important;
	}*/

	.send > form {
		margin: 0 1rem;
		border-top: 1px solid var(--blend-20);
/*		padding: 0.5rem 0 0.5rem 0.5rem;*/
		padding: 0.5rem 0;
		display: flex;
		align-items: flex-start;
		position: relative;
		gap: 1rem;
	}

	.send.fullscreen > form {
		flex-direction: column;
	}

	.private .send > form {
		border-top: 0;
		padding-top: 1rem;
		padding-bottom: 0;
		margin: 0;
	}

	@media (max-width: 700px) {
		.private .send > form {
			border-top: 1px solid var(--blend-40);
		}
	}

	.private .send.fullscreen > form {
		margin: 0 1rem;
	}

	:global(.report) .private .send > form {
		border-top: 1px solid var(--blend-40);
	}

	.counter {
		display: none;
		font-size: 0.8rem;;
		line-height: 1;
		position: absolute;
		bottom: 0.6rem;
		right: 0;
		color: var(--blend-60);
		width: 1.5rem;
		text-align: center;
	}
	.counter.over {
		color: var(--errorColor);
	}
	.send textarea {
		border: 0;
		padding: 0;
		width: 100%;
		flex: 1 1 auto;
		min-height: 3.2rem;
		max-height: 15rem;
		background: none;
		resize: none;
		font-family: Inter;
		font-size: 1rem;
		border-radius: 0;
		color: var(--textColor);
		text-align: left;
	}

	.private .send textarea {
		min-height: 3rem;
		max-height: 3rem;
	}

	.send textarea:focus {
		outline: none;
	}

	.send button {
		flex: 0 0 auto;
		margin-left: auto;
		padding: 0;
		border: 0;
		background: transparent;
		width: 2rem;
		height: 2rem;
		position: relative;
		box-sizing: border-box;
		border-radius: 3px;
		color: var(--bgColor);
		background: var(--accentColor);
	}
	.send button > svg {
		position: absolute;
		top: 0.15rem;
		left: 0.15rem;
		width: calc(100% - 0.3rem);
		height: calc(100% - 0.3rem);
	}

	:global(.report) .private .send button {
		background: var(--textColor);
	}

	/*.send button:hover,
	.send button:active,
	.send button:focus {
		cursor: pointer;
		color: var(--bgColor);
		background: var(--textColor);
	}*/
	
	::-webkit-input-placeholder { /* Chrome/Opera/Safari */
	  color: var(--blend-60);
	}
	::-moz-placeholder { /* Firefox 19+ */
	  color: var(--blend-60);
	}
	:-ms-input-placeholder { /* IE 10+ */
	  color: var(--blend-60);
	}
	:-moz-placeholder { /* Firefox 18- */
	  color: var(--blend-60);
	}
	.emoji > :global(p),
	textarea.emoji {
		font-size: 1.4rem;
	}
	.msg.pinned {
		/*position: sticky;*/
		/*top: -1px;*/
		position: absolute;
		top: 0;
		z-index: 1000;
		width: 100%;
	}
	.msg.pinned > div {
		box-shadow: inset 0 0 2px rgba(0,0,0,0.05);
		background: var(--blend-05);
	}
	.msg.organiser.pinned > div {
		background: var(--accentBlend-10);
	}
	.private .msg.pinned > div {
		background: var(--accentBlend-10);
	}

	.msg.pinned > div:after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;
		box-shadow: 0 5px 5px -3px rgba(0,0,0,0.15);
		/*opacity: 0;*/
		/*transition: opacity 0.3s ease;*/
	}
	/*.msg.pinned:global(.at-top) > div:after {
		opacity: 1;
	}*/
	.gif {
		max-width: 100%;
	}
	.msg.muted {
		display: none !important;
	}

	/*.msg :global(.menu) {
		position: absolute;
		left: 2.4rem;
		top: 0.3rem;
		width: 100px;
	}

	.msg :global(.menu.arrow:before) {
		top: 0.7rem !important;
		left: -0.5rem !important;
	}*/

	.msg:last-child:not(:first-child) :global(.menu) {
		top: auto;
		bottom: -0.5rem;
	}

	.msg:last-child:not(:first-child) :global(.menu.arrow:before) {
		top: auto !important;
		bottom: 0.7rem !important;
	}

	/*.msg:not(.organiser) :global(.avatar) {
		cursor: pointer;
	}*/

	/*.chat .slate {
		padding: 2rem;
	}*/

	/*.send {
		transition: transform 0.4s ease;
		transform: translateY(10px);
		position: relative;
		top: -10px;
	}*/

	.send.fullscreen {
		position: fixed;
		top: auto;
		left: 0;
		width: 100%;
/*		height: 88dvh;*/
		height: calc(100dvh - calc(var(--mh) + 5px));
		bottom: 0;
		background: var(--bgColor);
		z-index: 9000;
		padding-top: 4rem;
		box-shadow: 0 0 10px var(--shadow);
		border-top-left-radius: 16px;
		border-top-right-radius: 16px;
		box-shadow: 0 0 2rem var(--shadow, #000);
		border: 1px solid var(--blend-10, #eee);
		border-bottom: none;
		box-sizing: border-box;
	}

	@media all and (min-width: 800px) {
		.send.fullscreen {
			max-width: 800px;
			left: calc(50vw - 400px);
		}
	}

	.send.fullscreen > form {
		border: none !important;
	}

	/*.send.fullscreen > :global(button.subtle) {
		position: absolute;
		top: 0.5rem;
		left: -0.2rem;
		width: 2rem;
		height: 2rem;
		color: var(--textColor);
	}

	.send.fullscreen > :global(button.subtle svg) {
		width: 2rem;
		height: 2rem;
		inset: unset;
	}*/

	.send.fullscreen .counter {
		display: block;
		bottom: auto;
		top: -2.4rem;
		right: auto;
		left: 0;
		text-align: left;
	}

	.likes {
		display: flex;
		margin-top: 0.6rem;
		line-height: 1;
		margin-bottom: -0.1em;
		justify-content: space-between;
		padding: 0.5rem 0.5rem 0.25rem 0;
	}
	.likes span {
		vertical-align: middle;
		/*color: var(--blend-60);*/
		font-size: 0.8rem;
		font-weight: 600;
	}
	.likes svg {
		display: inline-block;
		/*width: 1em;
		height: 1em;*/
		width: 1.8em;
		height: 1.8em;
		vertical-align: middle;
		/*margin-left: 0.1em;*/
	}

	.likes button {
		display: flex;
		cursor: pointer;
		align-items: center;
		justify-content: flex-start;
		gap: 0.2em;
		padding: 0;
		border: 0;
		background: transparent;
		color: var(--textColor);
		font-size: 0.9rem;
	}

	.likes button:hover {
		color: var(--accentColor);
	}

	.likes > span {
		display: flex;
		gap: 1.4rem;
	}

	.likes button.lit {
		color: var(--accentColor);
	}

	.likes button.lit.spotlight {
		color: var(--errorColor);
	}

	.likes span.buttonLabel {
		font-weight: 400;
	}

	.new {
		position: fixed;
		bottom: calc(2rem + env(safe-area-inset-bottom));
		right: 2rem;
	}
	.new :global(button) {
		width: 3.6rem !important;
		height: 3.6rem !important;
		box-shadow: 0 4px 8px var(--darkShadow);
	}
	.new :global(button > svg) {
		width: 2.8rem !important;
		height: 2.8rem !important;
	}

</style>

<!-- <svelte:window on:click={handleWindowClick} on:keydown|stopPropagation={handleKeydown}/> -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<svelte:window on:click={handleWindowClick}/>

{#if mounted}
	<div class="wrap" in:fly={{ duration: 300, y: 20 }} class:private={isPrivate}>

		<div class="chat" class:ready style="padding-top:{pinHeight}px;">
			<div bind:this={msgs}>

				{#each messages as msg (msg.index)}
					<div
						id="msg-{msg.index}"
						class="msg"
						class:pinned={msg.pinned}
						class:organiser={participants[msg.sender].type == 'login'}
						class:muted={$shadowbans.includes(msg.sender) && (msg.sender != $attendee.ref)}
						class:openMenu={avatarMenu == msg.index}
						in:receive|local="{{ key: msg.index }}"
						out:send|local="{{ key: msg.index }}"
						animate:flip="{{ duration: 200 }}"
					>
						<div>
							{#if participants[msg.sender] && participants[msg.sender].type && (participants[msg.sender].type == 'login') && (!participants[msg.sender].f || !participants[msg.sender].l)}
								<Avatar
									bind:this={avatars[msg.index]}
									identity={participants[msg.sender]}
									anon={true}
								>
									{#if $event.theme.favicon && $event.theme.favicon.filename}
										<img
											class="icon"
											src="https://cdn.attendzen.io/{$event.accountRef}/{$event.theme.favicon.filename}"
											alt={$event.name}
											crossorigin="anonymous"
										/>
									{/if}
								</Avatar>
							{:else}
								<Avatar
									bind:this={avatars[msg.index]}
									identity={participants[msg.sender]}
									hasMenu={participants[msg.sender] && participants[msg.sender].type && (participants[msg.sender].type != 'login')}
									on:open={() => { avatarMenu = msg.index; closeAvatarMenus(); }}
									on:close={() => { avatarMenu = null }}
								/>
							{/if}
							<p class="person">
								{#if participants[msg.sender]}
									{#if (participants[msg.sender].type == 'login') && (!participants[msg.sender].f || !participants[msg.sender].l)}
										<strong>{$event.name}</strong>
									{:else}
										<strong>{participants[msg.sender].f} {participants[msg.sender].l}</strong>
									{/if}
									{#if msg.pinned}<svg viewBox="0 0 32 32"><path d="M30.91 12.26l-11.17-11.17c-.78-.78-2.05-.78-2.83 0-.78.78-.78 2.05 0 2.83l1.46 1.46-9.92 8-1.05-1.06c-.78-.78-2.05-.78-2.83 0-.78.78-.78 2.05 0 2.83l5.43 5.43-9.21 9.21c-.39.39-.39 1.02 0 1.41.2.2.45.29.71.29s.51-.1.71-.29l9.21-9.21 5.43 5.43c.39.39.9.59 1.41.59s1.02-.2 1.41-.59c.78-.78.78-2.05 0-2.83l-1.05-1.05 8-9.92 1.46 1.46c.39.39.9.59 1.41.59s1.02-.2 1.41-.59c.8-.78.8-2.04.01-2.82z"/></svg>{/if}
									{#if (!isPrivate && (participants[msg.sender].type == 'login'))}<em>Organiser</em>{/if}
								{/if}
								<span>{showDate(msg.sent, 'HH:mm')}</span>
							</p>
							{#if msg.gif}
								<div class="message gif">
									<video class="gif" autoplay loop muted inline webkit-playsinline playsinline>
										<source src={msg.gif} type="video/mp4">
									</video>
								</div>
							{:else}
								<div class="message" class:emoji={(msg.message.length == 9) && (/\p{Extended_Pictographic}/u.test(msg.message))}>
									{@html msg.message}
								</div>
							{/if}
							{#key msg.revision}
								<p class="likes">
									<span>
										<button type="button" on:click|stopPropagation={toggleLike(msg)} use:autoblur>
											<svg viewBox="0 0 20 20">
												{#if msg.liked}
													<path d="m8.3 14.7c.6.5 1.3.5 1.3.5h4.5c.7 0 1.2-.6 1.2-1.3 0-.1 0-.2 0-.3.5-.2.8-.7.8-1.2 0-.2 0-.4-.1-.5.4-.2.6-.7.6-1.1s-.1-.7-.4-.9c.2-.2.3-.5.3-.8s-.1-.7-.4-.9c-.3-.3-.6-.4-1-.4h-2.8l.2-1.2c0-.2 0-.3 0-.4 0-1.2-.8-2.5-1.8-2.5-.3 0-.6.1-.8.3v-.1l.1 1.9v.2c0 .1 0 .2-.1.2l-1.6 3z"/><path d="m6.9 15.3c.3 0 .6-.2.6-.6v-5.7c0-.3-.2-.6-.6-.6h-3c-.3.1-.6.3-.6.6v5.7c0 .3.2.6.5.6z"/>
												{:else}
													<path d="m10.1 15.15c-.09 0-.87-.02-1.5-.55l-.18-.15v-4.97c0-.25.07-.51.19-.73l1.36-2.52-.14-2.02.29-.24c.29-.25.67-.38 1.05-.38 1.22 0 2.13 1.47 2.13 2.79 0 .16-.01.33-.04.48l-.08.49h1.98c.46 0 .91.19 1.24.52.32.32.49.74.49 1.18 0 .24-.05.47-.15.68.17.27.26.58.26.89 0 .46-.18.89-.5 1.2.01.09.02.18.02.28 0 .55-.26 1.05-.69 1.36v.01c0 .9-.7 1.63-1.59 1.68zm-.68-1.19c.33.19.68.19.68.19h4.1c.35-.02.63-.32.63-.69 0-.06-.01-.11-.02-.16l-.1-.41.39-.17c.25-.11.42-.36.42-.63 0-.09-.02-.18-.05-.27l-.17-.4.37-.22c.21-.12.33-.34.33-.59 0-.18-.08-.36-.21-.49l-.32-.31.28-.35c.09-.12.14-.26.15-.42 0-.17-.07-.34-.2-.47-.14-.14-.33-.22-.53-.22h-3.14l.25-1.64c.02-.11.03-.22.03-.33 0-.87-.58-1.79-1.13-1.79-.11 0-.21.03-.31.08l.11 1.48c.01.13 0 .25-.03.38-.03.12-.08.24-.14.35l-1.32 2.35c-.04.08-.07.16-.07.25z"/><path d="m6.44 15.42h-2.41c-.55 0-.99-.44-.99-.99v-5.15c0-.55.44-.99.99-.99h2.41c.55 0 .99.44.99.99v5.14c-0 .55-.44 1-.99 1zm-.51-6.14h-1.4c-.28 0-.5.22-.5.5l.01 4.14c0 .28.23.5.5.5h1.4c.28 0 .5-.22.5-.5v-4.14c-.01-.27-.23-.5-.51-.5z"/>
												{/if}
											</svg>
											<span>{msg.likes}</span>
										</button>
										{#if mod}
											<button type="button" class:lit={msg.pinned} on:click|stopPropagation={togglePin(msg)} use:autoblur>
												<svg viewBox="0 0 20 20"><title>Pin</title><path d="M16.44 8.24l-4.47-4.47c-.35-.35-.91-.35-1.25 0-.35.35-.35.91 0 1.25l.58.58-3.83 3.09-.43-.42c-.35-.35-.91-.35-1.25 0-.35.35-.35.91 0 1.25l2.14 2.14-4.06 4.06c-.17.17-.17.45 0 .63.09.09.2.13.31.13s.23-.04.31-.13l4.06-4.06 2.14 2.14c.17.17.4.26.63.26s.45-.09.63-.26c.35-.35.35-.91 0-1.25l-.43-.43 3.09-3.83.58.58c.17.17.4.26.63.26s.45-.09.63-.26c.34-.35.34-.92-.01-1.26z"/></svg>
												<span class="buttonLabel">
													{#if msg.pinned}
														Unpin
													{:else}
														Pin
													{/if}
												</span>
											</button>
										{/if}
										{#if (!isPrivate && mod) || (($attendee.type != 'login') && (msg.sender == $attendee.ref))}
											<button type="button" on:click|stopPropagation={actionDelete(msg)} use:autoblur>
												<svg viewBox="0 0 20 20"><title>Delete</title><path d="M12 14.5c-.28 0-.5-.22-.5-.5v-5.01c0-.28.22-.5.5-.5s.5.22.5.5v5.01c0 .28-.22.5-.5.5zM10 14.5c-.28 0-.5-.22-.5-.5v-5c0-.28.22-.5.5-.5s.5.22.5.5v5.01c0 .27-.22.49-.5.49zM8 14.5c-.28 0-.5-.22-.5-.5v-5c0-.28.22-.5.5-.5s.5.22.5.5v5.01c0 .27-.22.49-.5.49zM15.5 5.5c0-.55-.45-1-1-1h-2.49c0-.55-.45-1-1-1h-2c-.55 0-1 .45-1 1h-2.51c-.55 0-1 .45-1 1v2h1v8c0 .55.45 1 1 1h7c.55 0 1-.45 1-1v-8h1v-2zm-2 9.5c0 .28-.22.5-.5.5h-6c-.28 0-.5-.22-.5-.5v-7.5h7v7.5zm1-8.5h-9v-.49c0-.28.22-.5.5-.5h8c.28 0 .5.22.5.5v.49z"/></svg>
												<span class="buttonLabel">Delete</span>
											</button>
										{/if}
									</span>
									<span>
										<button type="button" class="spotlight" on:click|stopPropagation={toggleSpotlight(msg)} use:autoblur class:lit={$spotlight && ($spotlight.type == 'chat') && ($spotlight.index == msg.index)}>
											{#if $spotlight && ($spotlight.type == 'chat') && ($spotlight.index == msg.index)}
												<span class="buttonLabel">Take off screen</span>
											{:else}
												<span class="buttonLabel">Show on screen</span>
											{/if}
											<svg viewBox="0 0 80 80">
												{#if $spotlight && ($spotlight.type == 'chat') && ($spotlight.index == msg.index)}
													<path d="m69.6 22.4c0-1.3-1.1-2.6-2.6-2.8-8.1-1.1-27-1.1-27-1.1s-18.9 0-27 1.1c-1.5.2-2.6 1.5-2.6 2.8-.4 8.6-.4 17.6-.4 17.6s0 9 .4 17.6c0 1.3 1.1 2.6 2.6 2.8 8.1 1.1 27 1.1 27 1.1s18.9 0 27-1.1c1.3-.2 2.4-1.3 2.6-2.8.4-8.6.4-17.6.4-17.6s0-9-.4-17.6zm-19.2 20.1h-20.9v-4.9h20.9z"/>
												{:else}
													<path d="m42.4 29.5v8.1h8v4.9h-8v8h-4.8v-8h-8v-4.9h8v-8.1z"/><path d="m40 22.6c.2 0 15.5 0 24.4.9.9 0 1.5.7 1.5 1.5.2 7.6.2 15 .2 15s0 7.4-.2 15c0 .9-.7 1.5-1.5 1.5-8.9.9-24.2.9-24.4.9s-15.5 0-24.4-.9c-.9 0-1.5-.7-1.5-1.5-.2-7.6-.2-14.8-.2-15 0 0 0-7.4.2-15 0-.9.7-1.5 1.5-1.5 8.9-.9 24.2-.9 24.4-.9m0-4s-18.9 0-27 1.1c-1.5.2-2.6 1.5-2.6 2.8-.4 8.5-.4 17.5-.4 17.5s0 9 .4 17.6c0 1.3 1.1 2.6 2.6 2.8 8.1 1.1 27 1.1 27 1.1s18.9 0 27-1.1c1.3-.2 2.4-1.3 2.6-2.8.4-8.6.4-17.6.4-17.6s0-9-.4-17.6c0-1.3-1.1-2.6-2.6-2.8-8.1-1-27-1-27-1z"/>
												{/if}
											</svg>
										</button>
									</span>
								</p>
							{/key}
							<!-- <div class="actions">
								<div>
									{#if mod || (msg.sender == $attendee.ref)}
										<button type="button" on:click|stopPropagation={actionDelete(msg)} use:autoblur>
											<svg viewBox="0 0 20 20"><title>Delete</title><path d="M12 14.5c-.28 0-.5-.22-.5-.5v-5.01c0-.28.22-.5.5-.5s.5.22.5.5v5.01c0 .28-.22.5-.5.5zM10 14.5c-.28 0-.5-.22-.5-.5v-5c0-.28.22-.5.5-.5s.5.22.5.5v5.01c0 .27-.22.49-.5.49zM8 14.5c-.28 0-.5-.22-.5-.5v-5c0-.28.22-.5.5-.5s.5.22.5.5v5.01c0 .27-.22.49-.5.49zM15.5 5.5c0-.55-.45-1-1-1h-2.49c0-.55-.45-1-1-1h-2c-.55 0-1 .45-1 1h-2.51c-.55 0-1 .45-1 1v2h1v8c0 .55.45 1 1 1h7c.55 0 1-.45 1-1v-8h1v-2zm-2 9.5c0 .28-.22.5-.5.5h-6c-.28 0-.5-.22-.5-.5v-7.5h7v7.5zm1-8.5h-9v-.49c0-.28.22-.5.5-.5h8c.28 0 .5.22.5.5v.49z"/></svg>
										</button>
									{/if}
									{#if mod}
										<button type="button" on:click|stopPropagation={togglePin(msg)} use:autoblur>
											<svg viewBox="0 0 20 20"><title>Pin</title><path d="M16.44 8.24l-4.47-4.47c-.35-.35-.91-.35-1.25 0-.35.35-.35.91 0 1.25l.58.58-3.83 3.09-.43-.42c-.35-.35-.91-.35-1.25 0-.35.35-.35.91 0 1.25l2.14 2.14-4.06 4.06c-.17.17-.17.45 0 .63.09.09.2.13.31.13s.23-.04.31-.13l4.06-4.06 2.14 2.14c.17.17.4.26.63.26s.45-.09.63-.26c.35-.35.35-.91 0-1.25l-.43-.43 3.09-3.83.58.58c.17.17.4.26.63.26s.45-.09.63-.26c.34-.35.34-.92-.01-1.26z"/></svg>
										</button>
									{/if}
									<button type="button" on:click|stopPropagation={toggleLike(msg)} use:autoblur>
										<svg viewBox="0 0 20 20"><title>Like</title><path d="M8.26 14.73c.56.48 1.28.48 1.31.48h4.55c.7-.04 1.24-.62 1.24-1.31 0-.11-.01-.21-.04-.31.48-.2.8-.67.8-1.21 0-.18-.04-.35-.1-.51.39-.23.63-.66.63-1.12 0-.36-.15-.7-.4-.94.18-.23.28-.51.28-.8 0-.34-.13-.67-.38-.92-.26-.26-.61-.41-.98-.41h-2.84l.18-1.18c.02-.15.03-.3.03-.45 0-1.17-.79-2.53-1.81-2.53-.29 0-.58.1-.81.29l-.09.07.15 1.95c.01.08 0 .17-.02.24l-.09.23-1.56 2.89-.05.05v5.49zM6.93 15.5c.3 0 .55-.24.55-.55v-5.7c0-.3-.24-.55-.55-.55h-3.03c-.3 0-.55.24-.55.55v5.7c0 .3.24.55.55.55h3.03z"/></svg>
									</button>
								</div>
							</div> -->
						</div>
					</div>
				{:else}
					<Slate>
						{#if !isPrivate}
							<p class="icon"><svg viewBox="0 0 32 32"><path d="M17.13 13.22c-.62 0-1.12-.51-1.12-1.12 0-.63.51-1.12 1.12-1.12.63 0 1.12.5 1.12 1.12 0 .61-.49 1.12-1.12 1.12zm-3.23 0c-.62 0-1.12-.51-1.12-1.12 0-.63.51-1.12 1.12-1.12.63 0 1.12.5 1.12 1.12.01.61-.49 1.12-1.12 1.12zm-3.22 0c-.61 0-1.12-.51-1.12-1.12 0-.63.51-1.12 1.12-1.12.63 0 1.12.5 1.12 1.12 0 .61-.49 1.12-1.12 1.12zM3.15 21.92c-1.74 0-3.15-1.46-3.15-3.25v-13.52c0-1.74 1.37-3.15 3.06-3.15h21.78c1.75 0 3.16 1.46 3.16 3.25v13.43c0 1.79-1.41 3.25-3.15 3.25l-5.75.01v6.21l-9.65-6.21-6.3-.02zm6.88-2l7.06 4.55v-4.55l7.75-.01c.58 0 1.15-.62 1.15-1.38v-13.15c.01-.76-.57-1.38-1.28-1.38h-21.51c-.66 0-1.2.58-1.2 1.29v13.26c0 .76.44 1.38 1.29 1.38l6.74-.01z"/></svg></p>
							<p class="label"><strong>No chat yet.</strong></p>
						{/if}
						<p>{slateText}</p>
					</Slate>
				{/each}

			</div>
		</div>

		{#if fullscreenSend}
			<div class="send" class:fullscreen={fullscreenSend} style="--mh:{mastheadHeight}px">
				{#if fullscreenSend}
					<Button close={true} on:click={() => { fullscreenSend = false }}/>
				{/if}
				<form on:submit|preventDefault={sendMessage}>
					{#if gif}
						<GifPreview {gif} on:cancel={unsetGif} on:send={sendMessage}/>
					{:else}
						<textarea
							bind:this={ta}
							bind:value={messageText}
							use:autoresize
							placeholder={isPrivate ? "Type your message" : "Say something …"}
							on:keypress={checkMessage}
							on:touchstart={makeFullscreen}
							on:mousedown={makeFullscreen}
							class:emoji={(messageText.length <= 3) && (/\p{Extended_Pictographic}/u.test(messageText))}
						></textarea>
						<!-- {#if emojis}
							<Emoji on:emoji={addEmoji} bind:showPanel={emojiPanel}/>
						{/if} -->
					{/if}
					{#if isPrivate || messageText || gif}
						<button type="submit" use:autoblur>
							<svg viewBox="0 0 80 80"><path d="M74.9 15.18l-.06-.29c-.07-.26-.18-.51-.31-.74l-.16-.38-.28-.21c-.18-.19-.38-.35-.6-.49l-.29-.16c-.22-.11-.44-.19-.68-.24-.13-.03-.26-.05-.34-.05-.24-.03-.48-.03-.7 0l-.26.04-.14.02-63.81 19.11c-1.26.38-2.15 1.51-2.22 2.82-.07 1.31.7 2.53 1.82 3.01l21.96 10.49-.45 15.95-.01.23.01.09c.01.19.03.37.06.54l.09.35c.07.21.17.42.3.63l.15.24.15.22.16.13.27.24.51.34.1.03c.14.06.29.12.43.16l.26.07c.22.04.42.06.63.06h.14l.26-.02c.75-.08 1.43-.44 1.95-1.01l11.42-10.4 13.23 6.32c.44.22.92.34 1.41.34l.27-.01c.34-.03.67-.11.97-.25.88-.38 1.52-1.12 1.77-2.05l11.92-43.79.07-.31.02-.19v-.01l.02-.3c.01-.21-.01-.4-.04-.53zm-9.93 3.5l-34.3 25.88-20.07-9.59 54.37-16.29zm-32.54 43.58l.34-12.26 8.6 4.11-8.94 8.15zm26.91-4l-24.6-11.75 34.98-26.4-10.38 38.15z"/></svg>
						</button>
						{#if messageText}
							<span class="counter" class:over={remaining < 0}>{remaining}</span>
						{/if}
					{/if}
				</form>
			</div>
		{/if}

	</div>

	{#if !fullscreenSend && !$showSettings}
		<div class="new" in:fade={{ duration: 400, delay: 400 }} out:fade|local={{ duration: 200 }}>
			<Button on:click={makeFullscreen}>
				<svg viewBox="0 0 80 80"><path d="m31.96 59.32-12.66 3.33c-.55.15-1.14-.01-1.54-.42-.4-.4-.56-.99-.42-1.54l3.34-12.65z"/><path d="m49.05 19.64c3.12-3.12 8.19-3.12 11.31 0s3.12 8.19 0 11.31z"/><path d="m57.48 33.84-11.31-11.32-22.63 22.63 4.41 4.4 19.81-19.81 2.45 2.45-19.81 19.81 4.46 4.46z"/></svg>
			</Button>
		</div>
	{/if}
{/if}

{#if deleteMsgDialog}
	<Dialog
		{...deleteMsgDialog}
		on:escape={() => { deleteMsgDialog = null}}
		on:confirm={() => { doDelete(deleteMsgDialog.msg) }}
	/>
{/if}
